import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, FlatList, Platform, useWindowDimensions, ScrollView, StyleSheet, Image } from 'react-native';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { useNavigation } from '@react-navigation/native';
import { AntDesign, Entypo, Fontisto } from '@expo/vector-icons';
import WebView from 'react-native-webview';
import apis from '@api/api';
import DropDownPicker, { DropDownPickerProps } from 'react-native-dropdown-picker';
import moment from 'moment';
import InvoicePreview from './Components/InvoicePreviewer';
import htmlTemplate from './template/htmlTemplate';
import { SmartSaaSIcon } from '@assets/--native';
import RowComponent from '@components/Global/RowComponent/RowComponent';
import * as Print from 'expo-print';
import { shareAsync } from 'expo-sharing';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import DraggableFlatList from 'react-native-draggable-flatlist';
import styles from '@stylesheet';
import globalStyles from '@stylesheet';
import { includes } from 'lodash';
import RangePicker from '@components/Global/DateRangePicker/RangePicker';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { Separator, TextArea, XStack, YStack, Dialog } from 'tamagui';
import ExpandableSection from '@components/Global/ExpandableSection';
import { InvoiceTypes } from './types/InvoiceTypes';
import CustomDialog from '@components/UI/CustomDialog';
import CurrencyExtractor from '@utils/stringUtils/currencyList';
import { Feather } from '@expo/vector-icons';
import SwitchWithIcons from 'react-native-switch-with-icons';
import MediaSelector from '@components/Canvas/MediaSelector';

const internalStyles = StyleSheet.create({
  textInput: {
    flex: 1,
    minWidth: "100%",
    maxHeight: 50,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
  }
});

const InvoiceScreen = ({ route }) => {
  const item = route.params;
  const navigation = useNavigation();
  const invoiceViewRef = useRef<WebView>(null);
  const [preview, setPreview] = useState(false);
  const [itemList, setItemList] = useState([]);
  const [invoice, setInvoice] = useState<InvoiceTypes>(item || {});
  // Use a ref to store the latest invoice state so callbacks always have current values
  const latestInvoiceStateRef = useRef<InvoiceTypes>(item || {});
  // Use a ref to store the latest itemList so callbacks always have current values
  const latestItemListRef = useRef<any[]>([]);

  // Update the refs whenever state changes
  useEffect(() => {
    latestInvoiceStateRef.current = invoice;
  }, [invoice]);

  useEffect(() => {
    latestItemListRef.current = itemList;
  }, [itemList]);

  const [issueDate, setIssueDate] = useState(Date.now());
  const [dueDate, setDueDate] = useState(Date.now());
  const theme = themeSelector();
  const pickerRef = useRef<DropDownPickerProps<any> | null>(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [pickerItems, setPickerItems] = useState([]);
  const [taxValue, setTaxValue] = useState(20);
  // Use a ref to store the latest taxValue so callbacks always have current values
  const latestTaxValueRef = useRef<number>(20);

  // Update the ref whenever taxValue changes
  useEffect(() => {
    console.log('🔄 Tax value ref updated:', taxValue);
    latestTaxValueRef.current = taxValue;
  }, [taxValue]);

  const [subtotalValue, setSubtotalValue] = useState<string>('0');
  const [totalValue, setTotalValue] = useState<string>('0');
  const [taxAmount, setTaxAmount] = useState<string>('0');
  const [invoiceId, setInvoiceId] = useState(`SSINV-${item?.invoice_template_name?.substring(0, 3).toUpperCase() || "inv"}-${String(item?.used_count + 1 || 1).padStart(4, '0')}`);
  const [sending, setSending] = useState(false);
  const activeCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const [customVals, setCustomVals] = useState({});
  const scrollRef = useRef(null);
  // Flag to prevent auto-calculation when user manually edits totals
  const manualOverrideRef = useRef(false);
  const invoiceRef = useRef<WebView>(null);
  const invoiceFromSectionRef = useRef<any>(null);
  const billingToSectionRef = useRef<any>(null);
  const detailsSectionRef = useRef<any>(null);
  const totalsSectionRef = useRef<any>(null);
  const itemsSectionRef = useRef<any>(null);

  // Refs for all input fields
  const fieldRefs = {
    invoice_template_name: useRef<any>(null),
    from_email: useRef<any>(null),
    email: useRef<any>(null),
    address: useRef<any>(null),
    phone: useRef<any>(null),
    contact_information: useRef<any>(null),
    country_code: useRef<any>(null),
    bankName: useRef<any>(null),
    bankSortcode: useRef<any>(null),
    bankAccountNumber: useRef<any>(null),
    bankIban: useRef<any>(null),
    invoice_logo: useRef<any>(null),
    toCompanyName: useRef<any>(null),
    toName: useRef<any>(null),
    toAddress: useRef<any>(null),
    invoiceId: useRef<any>(null),
    date: useRef<any>(null),
    notes: useRef<any>(null),
    subtotal: useRef<any>(null),
    tax: useRef<any>(null),
    total: useRef<any>(null),
  };
  const [currency, setCurrency] = useState(item?.currencyCode || 'USD');
  // Use a ref to store the latest currency so callbacks always have current values
  const latestCurrencyRef = useRef<string>(item?.currencyCode || 'USD');

  // Update the ref whenever currency changes
  useEffect(() => {
    latestCurrencyRef.current = currency;
  }, [currency]);

  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [currencyItems, setCurrencyItems] = useState(() => {
    const currencyList = CurrencyExtractor.extract() || [];
    return currencyList.length > 0 ? currencyList : [
      { label: '$-US Dollar', value: 'USD' },
      { label: '€-Euro', value: 'EUR' },
      { label: '£-British Pound', value: 'GBP' },
      { label: '¥-Japanese Yen', value: 'JPY' }
    ];
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [customLogoUrl, setCustomLogoUrl] = useState(item?.invoice_logo || '');
  // Use a ref to store the latest customLogoUrl so callbacks always have current values
  const latestCustomLogoUrlRef = useRef<string>(item?.invoice_logo || '');

  // Update the ref whenever customLogoUrl changes
  useEffect(() => {
    latestCustomLogoUrlRef.current = customLogoUrl;
  }, [customLogoUrl]);

  const [invoiceSettings, setInvoiceSettings] = useState({
    showLogo: true,
    showSignature: true,
    showBankDetails: true,
    showNotes: true,
    showFooter: true,
  });
  // Use a ref to store the latest invoiceSettings so callbacks always have current values
  const latestInvoiceSettingsRef = useRef({
    showLogo: true,
    showSignature: true,
    showBankDetails: true,
    showNotes: true,
    showFooter: true,
  });

  // Update the ref whenever invoiceSettings changes
  useEffect(() => {
    latestInvoiceSettingsRef.current = invoiceSettings;
  }, [invoiceSettings]);

  useEffect(() => {
    const getServices = async () => await apis.getProductServiceList().then(res => {
      console.log(res);
      const productsServices = res.map(item => {
        const matchingName = Object.keys(item.data).find(key => key.includes('name') || key.includes('title'));
        const matchingDescription = Object.keys(item.data).find(key => key.includes('description'));
        const priceKey = Object.keys(item.data).find(key => key.includes('price')) || 'price';
        return {
          label: matchingName ? item.data[matchingName] : '',
          description: matchingDescription ? item.data[matchingDescription] : '',
          value: item._id,
          price: Number(item.data[priceKey] || 0),
          quantity: 1
        };
      })
      setPickerItems(productsServices.flat());
    })
    getServices();
  }, [])

  const defaultInvoice = useMemo(() => ({
    date: moment(issueDate).format('DD/MM/YYYY'),
    dueDate: moment(dueDate).format('DD/MM/YYYY'),
    invoiceId,
    ...item
  }), [issueDate, dueDate, invoiceId, item]);

  // Calculate totals whenever itemList changes
  // When items are added/removed/modified, recalculate subtotal and then tax/total
  // Skip recalculation if user has manually overridden totals
  useEffect(() => {
    // Skip if manual override is active
    if (manualOverrideRef.current) {
      console.log('🔄 Totals useEffect skipped - manual override active');
      return;
    }

    console.log('🔄 Totals useEffect triggered - itemList:', itemList);
    // Ensure itemList is an array
    const safeItemList = Array.isArray(itemList) ? itemList : [];
    console.log('📊 Safe itemList length:', safeItemList.length);

    // Ensure price and quantity are numbers
    const subtotal = safeItemList.reduce((acc, item: any) => {
      const quantity = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      const itemTotal = quantity * price;
      console.log('💰 Item:', { label: item.label, quantity, price, itemTotal });
      return acc + itemTotal;
    }, 0);
    const tax = subtotal * (taxValue / 100);
    const total = subtotal + tax;
    console.log('✅ Final totals:', { subtotal, tax, total, taxValue });
    setSubtotalValue(subtotal.toFixed(2));
    setTaxAmount(tax.toFixed(2));
    setTotalValue(total.toFixed(2));
  }, [itemList, taxValue]); // Recalculate when items or tax percentage changes

  const currentInvoice = useMemo(() => ({
    ...defaultInvoice,
    ...invoice,
    date: moment(issueDate).format('DD/MM/YYYY'),
    dueDate: moment(dueDate).format('DD/MM/YYYY'),
    invoiceId,
    contact_information: invoice?.contact_information || defaultInvoice.contact_information,
    items: itemList,
    currencyCode: currency,
    invoice_logo: customLogoUrl || invoice?.invoice_logo || defaultInvoice.invoice_logo,
  }), [defaultInvoice, invoice, itemList, currency, issueDate, dueDate, invoiceId, customLogoUrl]);


  const htmlContent = useMemo(() => {
    console.log('📄 htmlContent useMemo recalculating...');
    console.log('📄 Current state in useMemo:', {
      invoice_template_name: invoice?.invoice_template_name,
      from_email: invoice?.from_email,
      address: invoice?.address,
      invoiceId,
      issueDate,
      dueDate,
      itemListLength: itemList?.length,
      taxValue,
      currency,
    });
    console.log('📄 Full invoice object:', JSON.stringify(invoice, null, 2));
    console.log('📄 Full item object:', JSON.stringify(item, null, 2));

    // Ensure itemList is an array
    const safeItemList = Array.isArray(itemList) ? itemList : [];

    // Calculate totals directly from state to ensure we always have the latest values
    // Ensure price and quantity are numbers
    const calculatedSubtotal = safeItemList.reduce((acc, item: any) => {
      const quantity = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      return acc + (quantity * price);
    }, 0);
    const calculatedTax = calculatedSubtotal * (taxValue / 100);
    const calculatedTotal = calculatedSubtotal + calculatedTax;

    console.log('📄 Calculated totals in useMemo:', {
      calculatedSubtotal,
      calculatedTax,
      calculatedTotal,
    });

    // Construct the invoice object fresh to ensure we have latest values
    console.log('📄 Building freshInvoice - item:', { invoice_template_name: item?.invoice_template_name });
    console.log('📄 Building freshInvoice - invoice state:', { invoice_template_name: invoice?.invoice_template_name });

    // IMPORTANT: Spread invoice AFTER item so invoice state overrides item defaults
    const freshInvoice = {
      ...item,
      ...invoice, // This should override item values with current state
      date: moment(issueDate).format('DD/MM/YYYY'),
      dueDate: moment(dueDate).format('DD/MM/YYYY'),
      invoiceId,
      contact_information: invoice?.contact_information || item?.contact_information,
      items: safeItemList, // Use safe array
      currencyCode: currency,
      invoice_logo: customLogoUrl || invoice?.invoice_logo || item?.invoice_logo,
    };

    console.log('📄 After spreading - freshInvoice.invoice_template_name:', freshInvoice.invoice_template_name);

    console.log('📄 Fresh invoice object in useMemo:', {
      invoice_template_name: freshInvoice.invoice_template_name,
      from_email: freshInvoice.from_email,
      address: freshInvoice.address,
      date: freshInvoice.date,
      invoiceId: freshInvoice.invoiceId,
      itemsCount: freshInvoice.items?.length,
    });

    const generatedHtml = htmlTemplate(
      freshInvoice,
      calculatedSubtotal,
      calculatedTotal,
      calculatedTax,
      activeCompany,
      invoiceSettings
    );

    console.log('📄 Generated HTML length in useMemo:', generatedHtml?.length);

    // Extract the actual invoice title from the HTML to verify it's correct
    // The title is in an <h3> tag with data-field="invoice_template_name"
    const titleMatch = generatedHtml.match(/<h3[^>]*data-field="invoice_template_name"[^>]*>([^<]+)<\/h3>/i) ||
      generatedHtml.match(/<h3[^>]*>([^<]+)<\/h3>/i) ||
      generatedHtml.match(/data-field="invoice_template_name"[^>]*>([^<]+)/i);
    const extractedTitle = titleMatch ? titleMatch[1].trim() : 'NOT FOUND';
    console.log('📄 Extracted invoice title from HTML:', extractedTitle);
    console.log('📄 Expected invoice title:', freshInvoice.invoice_template_name);
    console.log('📄 Titles match?', extractedTitle === freshInvoice.invoice_template_name);
    console.log('📄 Generated HTML contains invoice_template_name:', generatedHtml?.includes(freshInvoice.invoice_template_name || ''));
    console.log('📄 Generated HTML contains invoiceId:', generatedHtml?.includes(invoiceId || ''));

    return generatedHtml;
  }, [item, invoice, issueDate, dueDate, invoiceId, itemList, currency, customLogoUrl, taxValue, activeCompany, invoiceSettings]);

  const printToFile = useCallback(async (getLatestHtml?: () => string) => {
    console.log('🖨️ printToFile called');
    console.log('🖨️ Has callback?', !!getLatestHtml, typeof getLatestHtml);

    // If a callback is provided, use it to get the latest HTML content
    // Otherwise, generate it fresh from current state
    let latestHtmlContent: string;

    if (getLatestHtml && typeof getLatestHtml === 'function') {
      console.log('🖨️ Using callback to get latest HTML');
      try {
        latestHtmlContent = getLatestHtml();
        console.log('🖨️ HTML from callback length:', latestHtmlContent?.length);
        console.log('🖨️ HTML from callback preview (first 200 chars):', latestHtmlContent?.substring(0, 200));

        // Extract title from HTML to verify
        // The title is in an <h3> tag with data-field="invoice_template_name"
        const titleMatch = latestHtmlContent.match(/<h3[^>]*data-field="invoice_template_name"[^>]*>([^<]+)<\/h3>/i) ||
          latestHtmlContent.match(/<h3[^>]*>([^<]+)<\/h3>/i) ||
          latestHtmlContent.match(/data-field="invoice_template_name"[^>]*>([^<]+)/i);
        const extractedTitle = titleMatch ? titleMatch[1].trim() : 'NOT FOUND';
        console.log('🖨️ Extracted title from callback HTML:', extractedTitle);
        console.log('🖨️ Current invoice_template_name in state:', invoice?.invoice_template_name);
        console.log('🖨️ Titles match?', extractedTitle === invoice?.invoice_template_name);
      } catch (error) {
        console.error('❌ Error getting HTML from callback:', error);
        latestHtmlContent = '';
      }
    } else {
      console.log('🖨️ Generating HTML from current state');
      console.log('🖨️ Current state values:', {
        invoice_template_name: invoice?.invoice_template_name,
        from_email: invoice?.from_email,
        address: invoice?.address,
        invoiceId,
        issueDate,
        dueDate,
        itemListLength: itemList?.length,
        itemList: itemList,
        taxValue: latestTaxValueRef.current, // Use ref for consistency
        taxValueFromState: taxValue, // Also log state for comparison
        currency,
        customLogoUrl,
      });

      // Force recalculation by constructing everything fresh from current state
      // This ensures we get the absolute latest values, even if React hasn't flushed updates yet
      // Use ref to get latest invoice state and itemList
      const latestInvoiceState = latestInvoiceStateRef.current;
      const latestInvoice = {
        ...item, // Base template data
        ...latestInvoiceState, // Current form values from ref
        date: moment(issueDate).format('DD/MM/YYYY'),
        dueDate: moment(dueDate).format('DD/MM/YYYY'),
        invoiceId,
        contact_information: latestInvoiceState?.contact_information || item?.contact_information,
        items: latestItemListRef.current, // Use ref to get latest itemList
        currencyCode: latestCurrencyRef.current, // Use ref to get latest currency
        invoice_logo: latestCustomLogoUrlRef.current || latestInvoiceState?.invoice_logo || item?.invoice_logo,
      };

      console.log('🖨️ Latest invoice object:', {
        invoice_template_name: latestInvoice.invoice_template_name,
        from_email: latestInvoice.from_email,
        address: latestInvoice.address,
        date: latestInvoice.date,
        invoiceId: latestInvoice.invoiceId,
        itemsCount: latestInvoice.items?.length,
      });

      // Use ref to get the latest itemList to ensure custom products are included
      const latestItemList = latestItemListRef.current;
      // Recalculate totals from current state
      // Ensure itemList is an array and use Number() conversion like useEffect does
      const safeItemList = Array.isArray(latestItemList) ? latestItemList : [];
      const latestSubtotal = safeItemList.reduce((acc, item: any) => {
        const quantity = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        return acc + (quantity * price);
      }, 0);
      const currentTaxValue = latestTaxValueRef.current;
      const latestTax = latestSubtotal * (currentTaxValue / 100); // Use ref to get latest taxValue
      const latestTotal = latestSubtotal + latestTax;

      console.log('🖨️ Calculated totals:', {
        latestSubtotal,
        latestTax,
        latestTotal,
        taxValue: currentTaxValue, // Use ref for consistency
        taxValueFromState: taxValue, // Also log state for comparison
        taxCalculation: `${latestSubtotal} * (${currentTaxValue} / 100) = ${latestTax}`,
        itemListLength: safeItemList.length,
        itemList: safeItemList,
      });

      // Update latestInvoice with the latest itemList
      const finalInvoice = {
        ...latestInvoice,
        items: safeItemList, // Ensure we use the latest itemList with custom products
      };

      // Use ref to get the latest invoiceSettings to ensure logo visibility is correct
      const latestInvoiceSettings = latestInvoiceSettingsRef.current;

      // Generate HTML with the latest values - always recalculate to ensure freshness
      latestHtmlContent = htmlTemplate(
        finalInvoice,
        latestSubtotal,
        latestTotal,
        latestTax,
        activeCompany,
        latestInvoiceSettings
      );

      console.log('🖨️ Generated HTML length:', latestHtmlContent?.length);
      console.log('🖨️ Generated HTML preview (first 200 chars):', latestHtmlContent?.substring(0, 200));
    }

    console.log('🖨️ Final HTML content length:', latestHtmlContent?.length);
    console.log('🖨️ Final HTML contains invoice_template_name:', latestHtmlContent?.includes(invoice?.invoice_template_name || ''));
    console.log('🖨️ Final HTML contains invoiceId:', latestHtmlContent?.includes(invoiceId || ''));

    if (Platform.OS === 'web') {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(latestHtmlContent); // write your invoice HTML
        printWindow.document.close();

        printWindow.onload = () => {
          const images = printWindow.document.images;
          let loadedCount = 0;
          const total = images.length;

          if (total === 0) {
            // No images, safe to print
            printWindow.focus();
            printWindow.print();
            return;
          }

          // Wait for all images to load
          for (let i = 0; i < total; i++) {
            if (images[i].complete) {
              loadedCount++;
            } else {
              images[i].onload = () => {
                loadedCount++;
                if (loadedCount === total) {
                  printWindow.focus();
                  printWindow.print();
                }
              };
              images[i].onerror = () => {
                // Still count errored images to avoid hanging
                loadedCount++;
                if (loadedCount === total) {
                  printWindow.focus();
                  printWindow.print();
                }
              };
            }
          }

          // If all were already loaded
          if (loadedCount === total) {
            printWindow.focus();
            printWindow.print();
          }
        };
      }
    } else {
      const { uri } = await Print.printToFileAsync({ html: latestHtmlContent });
      await shareAsync(uri, { UTI: '.pdf', mimeType: 'application/pdf' });
    }
  }, [item, invoice, issueDate, dueDate, invoiceId, itemList, currency, customLogoUrl, taxValue, activeCompany, invoiceSettings]);


  const changeQuantity = useCallback((index, quantity) => {
    const newItems = [...itemList];
    newItems[index].quantity = Number(quantity);
    manualOverrideRef.current = false; // Reset manual override when items change
    setItemList(newItems);
  }, [itemList]);

  const changePrice = useCallback((index, price) => {
    const newItems = [...itemList];
    newItems[index].price = Number(price) || 0; // Ensure price is always a number
    manualOverrideRef.current = false; // Reset manual override when items change
    setItemList(newItems);
  }, [itemList]);

  const changeItem = useCallback((index, item) => {
    const newItems = [...itemList];
    newItems[index].item = item;
    setItemList(newItems);
  }, [itemList]);

  const removeItem = useCallback((value) => {
    const newItems = [...itemList];
    manualOverrideRef.current = false; // Reset manual override when items change
    setItemList(newItems.filter(item => item.value !== value));
  }, [itemList]);


  const RenderListItem = ({ item, index }) => {
    const [quantity, setQuantity] = useState(item.quantity || "1");
    return (
      <View style={Platform.OS === 'web'
        ? { ...styles.centerEverything, width: '100%', flexDirection: 'row', backgroundColor: index % 2 ? (theme ? '#f1f5f9' : '#334155') : (theme ? '#ffffff' : '#1e293b'), gap: 5, padding: 8, borderRadius: 8, marginVertical: 4, minHeight: 50 }
        : [styles.centerEverything, { width: '100%', flexDirection: 'row', backgroundColor: index % 2 ? (theme ? '#f1f5f9' : '#334155') : (theme ? '#ffffff' : '#1e293b'), gap: 5, padding: 8, borderRadius: 8, marginVertical: 4, minHeight: 50 }]
      }>
        <Pressable style={{ width: 30, alignItems: 'center', justifyContent: 'center' }}>
          <Entypo name="dots-three-vertical" size={20} color={theme ? '#64748b' : '#94a3b8'} />
        </Pressable>
        <TextArea style={[internalStyles.textInput, { flex: 1, minWidth: 0, backgroundColor: theme ? '#f8fafc' : '#0f172a', color: theme ? '#1e293b' : '#f1f5f9' }]} editable={false} value={item.label} />
        <TextArea style={[internalStyles.textInput, { width: 60, backgroundColor: theme ? '#f8fafc' : '#0f172a', color: theme ? '#1e293b' : '#f1f5f9' }]} allowFontScaling value={String(quantity)} onChangeText={setQuantity} onBlur={() => changeQuantity(index, Number(quantity))} onSubmitEditing={() => changeQuantity(index, Number(quantity))} />
        <Pressable onPress={() => removeItem(item.value || item._id)} style={{ width: 30, alignItems: 'center', justifyContent: 'center' }}>
          <AntDesign name="delete" size={20} color={theme ? '#ef4444' : '#f87171'} />
        </Pressable>
      </View>
    )
  }

  const _handleSend = async () => {

  }

  // Handle field clicks from preview
  const handleFieldClick = useCallback((fieldName: string) => {
    console.log('🎯 handleFieldClick called with:', fieldName);

    // Map field names to their section refs and input refs
    const fieldToSectionMap: Record<string, { sectionRef: React.RefObject<any>; inputRef: React.RefObject<any>; sectionName: string }> = {
      'invoice_template_name': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.invoice_template_name, sectionName: 'Invoice From' },
      'from_email': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.from_email, sectionName: 'Invoice From' },
      'email': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.email, sectionName: 'Invoice From' },
      'address': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.address, sectionName: 'Invoice From' },
      'phone': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.phone, sectionName: 'Invoice From' },
      'contact_information': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.contact_information, sectionName: 'Invoice From' },
      'country_code': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.country_code, sectionName: 'Invoice From' },
      'bankName': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.bankName, sectionName: 'Invoice From' },
      'bankSortcode': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.bankSortcode, sectionName: 'Invoice From' },
      'bankAccountNumber': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.bankAccountNumber, sectionName: 'Invoice From' },
      'bankIban': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.bankIban, sectionName: 'Invoice From' },
      'invoice_logo': { sectionRef: invoiceFromSectionRef, inputRef: fieldRefs.invoice_logo, sectionName: 'Invoice From' },
      'toCompanyName': { sectionRef: billingToSectionRef, inputRef: fieldRefs.toCompanyName, sectionName: 'Billing To' },
      'toName': { sectionRef: billingToSectionRef, inputRef: fieldRefs.toName, sectionName: 'Billing To' },
      'toAddress': { sectionRef: billingToSectionRef, inputRef: fieldRefs.toAddress, sectionName: 'Billing To' },
      'invoiceId': { sectionRef: detailsSectionRef, inputRef: fieldRefs.invoiceId, sectionName: 'Details' },
      'date': { sectionRef: detailsSectionRef, inputRef: fieldRefs.date, sectionName: 'Details' },
      'notes': { sectionRef: detailsSectionRef, inputRef: fieldRefs.notes, sectionName: 'Details' },
      'subtotal': { sectionRef: totalsSectionRef, inputRef: fieldRefs.subtotal, sectionName: 'Totals' },
      'tax': { sectionRef: totalsSectionRef, inputRef: fieldRefs.tax, sectionName: 'Totals' },
      'total': { sectionRef: totalsSectionRef, inputRef: fieldRefs.total, sectionName: 'Totals' },
    };

    const fieldInfo = fieldToSectionMap[fieldName];
    if (!fieldInfo) {
      console.log('Field not mapped:', fieldName);
      return;
    }

    // Open the section if it's closed
    if (fieldInfo.sectionRef.current) {
      const isOpen = fieldInfo.sectionRef.current.getOpen();
      if (!isOpen) {
        fieldInfo.sectionRef.current.setOpen(true);
      }
    }

    // Scroll to and focus the input field after a short delay to allow UI to update
    setTimeout(() => {
      const inputRef = fieldInfo.inputRef.current;
      if (inputRef && scrollRef.current) {
        // For web platform
        if (Platform.OS === 'web') {
          // Get the DOM element
          const domElement = inputRef;

          if (domElement) {
            try {
              // Try to get the ScrollView's DOM element
              const scrollViewRef = scrollRef.current as any;
              const scrollViewElement = scrollViewRef?._component ||
                scrollViewRef?.getNode?.() ||
                scrollViewRef?.getScrollableNode?.() ||
                scrollViewRef;

              if (scrollViewElement) {
                // Get positions using DOM methods
                const inputRect = domElement.getBoundingClientRect();

                // Try to get ScrollView rect
                let scrollViewRect: DOMRect;
                let scrollViewScrollTop = 0;

                if (scrollViewElement.getBoundingClientRect) {
                  scrollViewRect = scrollViewElement.getBoundingClientRect();
                  scrollViewScrollTop = scrollViewElement.scrollTop || 0;
                } else {
                  // Fallback: use window position
                  scrollViewRect = { top: 0, left: 0, bottom: 0, right: 0, width: 0, height: 0 } as DOMRect;
                }

                // Calculate relative position
                const relativeTop = inputRect.top - scrollViewRect.top + scrollViewScrollTop;

                // Scroll the ScrollView using React Native's API first, then DOM fallback
                if (scrollRef.current && typeof scrollRef.current.scrollTo === 'function') {
                  scrollRef.current.scrollTo({
                    y: Math.max(0, relativeTop - 100),
                    animated: true
                  });
                } else if (scrollViewElement.scrollTo) {
                  scrollViewElement.scrollTo({
                    top: Math.max(0, relativeTop - 100),
                    behavior: 'smooth'
                  });
                }

                // Focus the input after scroll
                setTimeout(() => {
                  if (domElement && typeof domElement.focus === 'function') {
                    domElement.focus({ preventScroll: true });
                    if (domElement.select && typeof domElement.select === 'function') {
                      domElement.select();
                    }
                  }
                }, 200);
              } else {
                // Fallback: just focus without scrolling
                if (domElement && typeof domElement.focus === 'function') {
                  domElement.focus({ preventScroll: true });
                }
              }
            } catch (error) {
              console.warn('Error scrolling to input:', error);
              // Fallback: just focus without scrolling
              if (domElement && typeof domElement.focus === 'function') {
                domElement.focus({ preventScroll: true });
              }
            }
          }
        } else {
          // For native platforms
          if (inputRef.measure && typeof inputRef.measure === 'function') {
            inputRef.measure((x, y, width, height, pageX, pageY) => {
              if (scrollRef.current) {
                scrollRef.current.scrollTo({ y: Math.max(0, pageY - 100), animated: true });
              }
            });
          }

          // Focus the input
          if (inputRef.focus && typeof inputRef.focus === 'function') {
            setTimeout(() => {
              inputRef.focus();
            }, 300);
          }
        }
      }
    }, 300);
  }, []);

  return (
    <DefaultScreen containerStyle={{ paddingBottom: 200 }} style={{ ...globalStyles.modernMainContent, paddingBottom: 50 }} backButton scrollDisabled searchBar={{
      showSearch: false, customStyle: { top: 20 }, otherComponents: [
        {
          function: () => {
            console.log('🔘 Preview button clicked');
            console.log('🔘 Current invoice state:', {
              invoice_template_name: invoice?.invoice_template_name,
              from_email: invoice?.from_email,
              address: invoice?.address,
            });
            console.log('🔘 htmlContent at button click:', {
              length: htmlContent?.length,
              containsInvoiceName: htmlContent?.includes(invoice?.invoice_template_name || ''),
              containsInvoiceId: htmlContent?.includes(invoiceId || ''),
            });

            // Extract title from HTML to see what's actually in it
            // The title is in an <h3> tag with data-field="invoice_template_name"
            const titleMatch = htmlContent.match(/<h3[^>]*data-field="invoice_template_name"[^>]*>([^<]+)<\/h3>/i) ||
              htmlContent.match(/<h3[^>]*>([^<]+)<\/h3>/i) ||
              htmlContent.match(/data-field="invoice_template_name"[^>]*>([^<]+)/i);
            const extractedTitle = titleMatch ? titleMatch[1].trim() : 'NOT FOUND';
            console.log('🔘 Extracted title from htmlContent:', extractedTitle);
            console.log('🔘 Expected title from state:', invoice?.invoice_template_name);
            console.log('🔘 Titles match?', extractedTitle === invoice?.invoice_template_name);

            printToFile(() => {
              console.log('🔘 Callback executed, generating fresh HTML from current state');
              // Use ref to get the absolute latest invoice state, not the closure value
              const currentInvoice = latestInvoiceStateRef.current;
              console.log('🔘 Current invoice state in callback (from ref):', {
                invoice_template_name: currentInvoice?.invoice_template_name,
                from_email: currentInvoice?.from_email,
                address: currentInvoice?.address,
              });
              console.log('🔘 Invoice state from closure (for comparison):', {
                invoice_template_name: invoice?.invoice_template_name,
              });

              // Generate fresh HTML from current state instead of using memoized htmlContent
              console.log('🔘 Callback - Reading current state values:', {
                invoiceState: currentInvoice,
                invoice_template_name: currentInvoice?.invoice_template_name,
                item_template_name: item?.invoice_template_name,
              });

              // Use ref to get the latest itemList to ensure custom products are included
              const latestItemList = latestItemListRef.current;
              const safeItemList = Array.isArray(latestItemList) ? latestItemList : [];
              console.log('🔘 Latest itemList from ref:', safeItemList);
              const calculatedSubtotal = safeItemList.reduce((acc, item: any) => {
                const quantity = Number(item.quantity) || 0;
                const price = Number(item.price) || 0;
                return acc + (quantity * price);
              }, 0);
              const currentTaxValue = latestTaxValueRef.current;
              const calculatedTax = calculatedSubtotal * (currentTaxValue / 100); // Use ref to get latest taxValue
              const calculatedTotal = calculatedSubtotal + calculatedTax;
              console.log('🔘 Preview callback - Tax calculation:', {
                subtotal: calculatedSubtotal,
                taxValue: currentTaxValue,
                taxValueFromState: taxValue,
                calculatedTax,
                calculatedTotal,
                taxCalculation: `${calculatedSubtotal} * (${currentTaxValue} / 100) = ${calculatedTax}`,
              });

              // IMPORTANT: Use ref to get latest invoice state, not closure value
              // Spread invoice AFTER item so invoice state overrides item defaults
              // Explicitly preserve invoice_template_name from invoice state to ensure it's not lost
              const freshInvoice = {
                ...item,
                ...currentInvoice, // Use ref value, not closure value
                // Explicitly preserve invoice_template_name from invoice state if it exists
                invoice_template_name: currentInvoice?.invoice_template_name || item?.invoice_template_name,
                date: moment(issueDate).format('DD/MM/YYYY'),
                dueDate: moment(dueDate).format('DD/MM/YYYY'),
                invoiceId,
                contact_information: currentInvoice?.contact_information || item?.contact_information,
                items: safeItemList, // Use latest itemList from ref
                currencyCode: latestCurrencyRef.current, // Use ref to get latest currency
                invoice_logo: latestCustomLogoUrlRef.current || currentInvoice?.invoice_logo || item?.invoice_logo,
              };

              console.log('🔘 Fresh invoice in callback:', {
                invoice_template_name: freshInvoice.invoice_template_name,
                from_item: item?.invoice_template_name,
                from_invoice_state: currentInvoice?.invoice_template_name,
              });

              // Use ref to get the latest invoiceSettings to ensure logo visibility is correct
              const latestInvoiceSettings = latestInvoiceSettingsRef.current;

              const freshHtml = htmlTemplate(
                freshInvoice,
                calculatedSubtotal,
                calculatedTotal,
                calculatedTax,
                activeCompany,
                latestInvoiceSettings
              );

              console.log('🔘 Generated fresh HTML length:', freshHtml?.length);

              // Extract title from fresh HTML
              const callbackTitleMatch = freshHtml.match(/<h3[^>]*data-field="invoice_template_name"[^>]*>([^<]+)<\/h3>/i) ||
                freshHtml.match(/<h3[^>]*>([^<]+)<\/h3>/i) ||
                freshHtml.match(/data-field="invoice_template_name"[^>]*>([^<]+)/i);
              const callbackExtractedTitle = callbackTitleMatch ? callbackTitleMatch[1].trim() : 'NOT FOUND';
              console.log('🔘 Extracted title from fresh HTML:', callbackExtractedTitle);
              console.log('🔘 Expected title from state:', invoice?.invoice_template_name);
              console.log('🔘 Titles match?', callbackExtractedTitle === invoice?.invoice_template_name);

              return freshHtml;
            });
          }, icon: <Fontisto name="preview" size={18} color="white" />, functionName: 'preview'
        },
        {
          function: () => {
            console.log('🔘 Share button clicked');
            console.log('🔘 Current invoice state:', {
              invoice_template_name: invoice?.invoice_template_name,
            });
            printToFile(() => {
              console.log('🔘 Share callback executed, generating fresh HTML from current state');
              // Use ref to get the absolute latest invoice state, not the closure value
              const currentInvoice = latestInvoiceStateRef.current;

              // Generate fresh HTML from current state instead of using memoized htmlContent
              // Use ref to get the latest itemList to ensure custom products are included
              const latestItemList = latestItemListRef.current;
              const safeItemList = Array.isArray(latestItemList) ? latestItemList : [];
              console.log('🔘 Share - Latest itemList from ref:', safeItemList);
              const calculatedSubtotal = safeItemList.reduce((acc, item: any) => {
                const quantity = Number(item.quantity) || 0;
                const price = Number(item.price) || 0;
                return acc + (quantity * price);
              }, 0);
              const currentTaxValue = latestTaxValueRef.current;
              const calculatedTax = calculatedSubtotal * (currentTaxValue / 100); // Use ref to get latest taxValue
              const calculatedTotal = calculatedSubtotal + calculatedTax;
              console.log('🔘 Share callback - Tax calculation:', {
                subtotal: calculatedSubtotal,
                taxValue: currentTaxValue,
                taxValueFromState: taxValue,
                calculatedTax,
                calculatedTotal,
                taxCalculation: `${calculatedSubtotal} * (${currentTaxValue} / 100) = ${calculatedTax}`,
              });

              // IMPORTANT: Use ref to get latest invoice state, not closure value
              // Spread invoice AFTER item so invoice state overrides item defaults
              // Also ensure we don't lose invoice_template_name if it exists in invoice state
              const freshInvoice = {
                ...item,
                ...currentInvoice, // Use ref value, not closure value
                // Explicitly preserve invoice_template_name from invoice state if it exists
                invoice_template_name: currentInvoice?.invoice_template_name || item?.invoice_template_name,
                date: moment(issueDate).format('DD/MM/YYYY'),
                dueDate: moment(dueDate).format('DD/MM/YYYY'),
                invoiceId,
                contact_information: currentInvoice?.contact_information || item?.contact_information,
                items: safeItemList, // Use latest itemList from ref
                currencyCode: latestCurrencyRef.current, // Use ref to get latest currency
                invoice_logo: latestCustomLogoUrlRef.current || currentInvoice?.invoice_logo || item?.invoice_logo,
              };

              // Use ref to get the latest invoiceSettings to ensure logo visibility is correct
              const latestInvoiceSettings = latestInvoiceSettingsRef.current;

              const freshHtml = htmlTemplate(
                freshInvoice,
                calculatedSubtotal,
                calculatedTotal,
                calculatedTax,
                activeCompany,
                latestInvoiceSettings
              );

              console.log('🔘 Generated fresh HTML for share, length:', freshHtml?.length);

              return freshHtml;
            });
          }, icon: <AntDesign name="share-alt" size={18} color="white" />, functionName: 'share'
        },
        { function: () => setPreview(!preview), icon: <AntDesign name="mail" size={18} color="white" />, functionName: 'email' },
        { function: () => setSettingsOpen(true), icon: <Feather name="settings" size={18} color="white" />, functionName: 'settings' },
        { function: () => { (navigation as any).navigate('InvoiceTemplates'); }, icon: <SmartSaaSIcon width={50} height={25} />, functionName: 'smartsaas' },
      ]
    }}>
      <XStack style={{ paddingTop: Platform.OS != 'web' ? 40 : 20, gap: 30, paddingBottom: Platform.OS != 'web' ? 50 : 20, flex: 1 }}>
        <ScrollView ref={scrollRef} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: Platform.select({ native: 10 }), gap: 20, paddingBottom: 20 }} style={{ maxWidth: 350 }}>
          <ExpandableSection ref={invoiceFromSectionRef} defaultState={true} title={'Invoice From'}>
            <View style={Platform.OS === 'web'
              ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), paddingBottom: 20 }
              : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { paddingBottom: 20 }]
            }>
              <RowComponent subtitle={'Invoice Title'} componentMinWidth={"100%"} customComponent={(<TextArea ref={fieldRefs.invoice_template_name} multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice?.invoice_template_name} onChangeText={(value) => {
                console.log('✏️ Invoice Title onChangeText called with value:', value);
                console.log('✏️ Current invoice state before update:', invoice);
                setInvoice((prevInvoice) => {
                  const updated = { ...prevInvoice, invoice_template_name: value };
                  console.log('✏️ Updated invoice state:', updated);
                  return updated;
                });
              }} />)} />
              <RowComponent subtitle={'Invoice Contact Email'} componentMinWidth={"100%"} customComponent={(<TextArea multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.from_email} onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, from_email: value
              }))} />)} />
              <RowComponent subtitle={'Invoice Contact Address'} componentMinWidth={"100%"} customComponent={(<TextArea multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.address} onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, address: value
              }))} />)} />
              <RowComponent subtitle={'Invoice Contact Number'} componentMinWidth={"100%"} customComponent={(<TextArea multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.contact_information?.phone} onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, contact_information: { ...invoice.contact_information, phone: value }
              }))} />)} />
              <RowComponent subtitle={'Invoice Country Code'} componentMinWidth={"100%"} customComponent={(<TextArea multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.country_code} onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, country_code: value
              }))} />)} />
              <RowComponent subtitle={'Bank Name'} componentMinWidth={"100%"} customComponent={(<TextArea ref={fieldRefs.bankName} multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.bankName} onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, bankName: value
              }))} />)} />
              <RowComponent subtitle={'Bank Sort Code'} componentMinWidth={"100%"} customComponent={(<TextArea ref={fieldRefs.bankSortcode} multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.bankSortcode} onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, bankSortcode: value
              }))} />)} />
              <RowComponent subtitle={'Bank Account Number'} componentMinWidth={"100%"} customComponent={(<TextArea ref={fieldRefs.bankAccountNumber} multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.bankAccountNumber} onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, bankAccountNumber: value
              }))} />)} />
              <RowComponent subtitle={'Bank IBAN'} componentMinWidth={"100%"} customComponent={(<TextArea ref={fieldRefs.bankIban} multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.bankIban} onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, bankIban: value
              }))} />)} />
            </View>
          </ExpandableSection>
          <ExpandableSection ref={billingToSectionRef} defaultState={true} title={'Billing To'}>
            <View style={Platform.OS === 'web'
              ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), paddingBottom: 20 }
              : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { paddingBottom: 20 }]
            }>
              <RowComponent subtitle={'Recipient Name / Company Name'} componentMinWidth={"100%"} customComponent={(<TextArea ref={fieldRefs.toCompanyName} multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.toCompanyName} onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, toCompanyName: value
              }))} />)} />
              <RowComponent subtitle={'Company Address'} componentMinWidth={"100%"} customComponent={(<TextArea ref={fieldRefs.toAddress} multiline={true} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.toAddress} onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, toAddress: value
              }))} />)} />
            </View>
          </ExpandableSection>
          <ExpandableSection ref={detailsSectionRef} title={'Details'}>
            <View style={Platform.OS === 'web'
              ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), paddingBottom: 20 }
              : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { paddingBottom: 20 }]
            }>
              <RowComponent subtitle={'Invoice ID'} componentMinWidth={"100%"} customComponent={(<TextArea ref={fieldRefs.invoiceId} multiline={false} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoiceId} onChangeText={(value) => setInvoiceId(value)} />)} />
              <RowComponent style={{ zIndex: 10000 }} subtitle={'Currency'} componentMinWidth={"100%"} customComponent={(
                <DropDownPicker
                  items={currencyItems}
                  open={currencyOpen}
                  setOpen={setCurrencyOpen}
                  value={currency}
                  setValue={(callback) => {
                    const newValue = typeof callback === 'function' ? callback(currency) : callback;
                    setCurrency(newValue as string);
                    setInvoice((prevInvoice) => ({
                      ...prevInvoice,
                      currencyCode: newValue as string
                    }));
                  }}
                  setItems={setCurrencyItems}
                  placeholder="Select currency"
                  searchable
                  zIndex={10000}
                  zIndexInverse={5000}
                />
              )} />
              <RangePicker startDate={new Date(issueDate)} endDate={new Date(dueDate)} dateCallback={({ startDate, endDate }) => { setIssueDate(startDate.getTime()); setDueDate(endDate.getTime()); }} />
              <RowComponent subtitle={'Notes'} componentMinWidth={"100%"} customComponent={(<TextArea ref={fieldRefs.notes} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={invoice.notes || ""} multiline placeholder='Add invoice notes here' onChangeText={(value) => setInvoice(invoice => ({
                ...invoice, notes: value
              }))} />)} />
            </View>
          </ExpandableSection>
          <ExpandableSection ref={itemsSectionRef} title={'Add Items'} >
            <View style={Platform.OS === 'web'
              ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), paddingVertical: 20, gap: 20, width: '100%', maxWidth: 350, overflow: 'hidden' }
              : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { paddingVertical: 20, gap: 20, width: '100%', maxWidth: 350, overflow: 'hidden' }]
            }>
              <View style={{ width: '100%', zIndex: 1000 }}>
                <DropDownPicker
                  items={pickerItems}
                  open={open}
                  setOpen={setOpen}
                  placeholder='Select a product or service'
                  value={selectedItem}
                  setValue={setSelectedItem}
                  categorySelectable
                  setItems={setPickerItems}
                  onSelectItem={(selectedItems: any) => {
                    // When multiple is true, selectedItems is an array of selected items
                    if (Array.isArray(selectedItems)) {
                      // Filter out items that are already in itemList
                      const existingIds = new Set(itemList.map((item: any) => item.value || item._id));
                      const newItems = selectedItems.filter((item: any) => !existingIds.has(item.value));
                      if (newItems.length > 0) {
                        manualOverrideRef.current = false; // Reset manual override when items change
                        setItemList((prevList: any[]) => [...prevList, ...newItems]);
                      }
                    } else if (selectedItems) {
                      // Single item selected (shouldn't happen with multiple=true, but handle it)
                      const existingIds = new Set(itemList.map((item: any) => item.value || item._id));
                      if (!existingIds.has((selectedItems as any).value)) {
                        manualOverrideRef.current = false; // Reset manual override when items change
                        setItemList((prevList: any[]) => [...prevList, selectedItems]);
                      }
                    }
                  }}
                  multiple={true}
                  searchable
                  onChangeSearchText={(text) => console.log(text)}
                  style={{ width: '100%' }}
                  containerStyle={{ width: '100%' }}
                  zIndex={10000}
                  zIndexInverse={5000}
                />
              </View>
              <CustomDialog
                onPress={(vals: any) => {
                  console.log('CustomDialog onPress called with vals:', vals);
                  //Append the new item to the list with a unique ID
                  // Ensure all required fields are present and properly formatted
                  // Spread vals first, then override with explicit values to ensure correct types
                  const newItem = {
                    ...vals, // Spread other values first
                    _id: new Date().getTime().toString() + Math.random().toString(36).substr(2, 9), // Generate unique ID
                    value: new Date().getTime().toString() + Math.random().toString(36).substr(2, 9), // For keyExtractor
                    price: Number(vals?.price) || 0, // Ensure price is always a number
                    quantity: Number(vals?.quantity) || 1, // Ensure quantity is always a number
                    label: vals?.product || vals?.label || '', // Use product field as label
                    description: vals?.description || '',
                  };
                  console.log('New item created:', newItem);
                  console.log('New item price (as number):', newItem.price, typeof newItem.price);
                  // Use functional update to ensure we get the latest state
                  manualOverrideRef.current = false; // Reset manual override when items change
                  setItemList((prevList: any[]) => {
                    const updatedList = [...prevList, newItem];
                    console.log('Previous list length:', prevList.length);
                    console.log('Updated list length:', updatedList.length);
                    console.log('Updated itemList:', updatedList);
                    return updatedList;
                  });
              }}
               title={'Add custom product'} schema={[
                { type: 'text', content: { helpText: "Custom product name" }, title: 'product' },
                { type: 'number', content: { helpText: "Custom product price" }, title: 'price' },
                { type: 'text', content: { helpText: "Custom product description" }, title: 'description' },
                { type: 'number', content: { helpText: "Custom product quantity" }, title: 'quantity' },
                ] as any} />
              <View style={{ width: '100%', maxHeight: 400 }}>
                <FlatList
                  data={itemList}
                  style={{ width: '100%' }}
                  contentContainerStyle={{ paddingBottom: 10 }}
                  keyExtractor={(item: any) => item._id || item.value || String(item)}
                  ListEmptyComponent={(<Pressable onPress={() => setOpen(true)}></Pressable>)}
                  renderItem={({ item, index }) => (<RenderListItem item={item} index={index} />)}
                  nestedScrollEnabled={true}
                />
              </View>
            </View>
          </ExpandableSection>
          <ExpandableSection ref={totalsSectionRef} openCallback={(openState) => {
            if (openState) {
              scrollRef.current?.scrollToEnd({ animated: true });
            }
          }} title={'Totals'} >
            <View style={Platform.OS === 'web'
              ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), paddingBottom: 20, maxWidth: 300 }
              : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { paddingBottom: 20, maxWidth: 300 }]
            }>
              <RowComponent title={'Subtotal'} customComponent={(<TextArea ref={fieldRefs.subtotal} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={subtotalValue} inputMode='numeric' onChangeText={(value: string) => {
                manualOverrideRef.current = true; // Enable manual override
                setSubtotalValue(value);
                // Recalculate tax and total based on new subtotal
                const newSubtotal = parseFloat(value) || 0;
                const newTax = newSubtotal * (taxValue / 100);
                const newTotal = newSubtotal + newTax;
                setTaxAmount(newTax.toFixed(2));
                setTotalValue(newTotal.toFixed(2));
              }} />)} />
              <RowComponent title={'Tax'}
                customComponent={(
                  <XStack flex={1} style={{ flexDirection: 'row', gap: 5, alignItems: 'center' }}>
                    <TextArea style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9', flex: 1 }]} inputMode='numeric' value={String(taxValue)} onChangeText={(value: string) => {
                      const numValue = parseFloat(value) || 0;
                      setTaxValue(numValue);
                      // Recalculate tax amount and total when percentage changes
                      const currentSubtotal = parseFloat(subtotalValue) || 0;
                      const newTax = currentSubtotal * (numValue / 100);
                      const newTotal = currentSubtotal + newTax;
                      setTaxAmount(newTax.toFixed(2));
                      setTotalValue(newTotal.toFixed(2));
                    }} />
                    <Text style={{ alignSelf: 'center', color: theme ? '#1e293b' : '#f1f5f9', marginHorizontal: 5 }}>%</Text>
                    <TextArea ref={fieldRefs.tax} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9', flex: 3 }]} value={taxAmount} inputMode='numeric' onChangeText={(value: string) => {
                      manualOverrideRef.current = true; // Enable manual override
                      setTaxAmount(value);
                      // Recalculate total when tax amount changes
                      const currentSubtotal = parseFloat(subtotalValue) || 0;
                      const newTax = parseFloat(value) || 0;
                      const newTotal = currentSubtotal + newTax;
                      setTotalValue(newTotal.toFixed(2));
                      // Update tax percentage if subtotal exists
                      if (currentSubtotal > 0) {
                        const newTaxPercentage = (newTax / currentSubtotal) * 100;
                        setTaxValue(newTaxPercentage);
                      }
                    }} />
                  </XStack>
                )} />
              <RowComponent title={'Total'} customComponent={(<TextArea ref={fieldRefs.total} style={[internalStyles.textInput, { backgroundColor: theme ? '#f8fafc' : '#1e293b', color: theme ? '#1e293b' : '#f1f5f9' }]} value={totalValue} inputMode='numeric' onChangeText={(value: string) => {
                manualOverrideRef.current = true; // Enable manual override
                setTotalValue(value);
                // Recalculate tax amount when total changes
                const currentSubtotal = parseFloat(subtotalValue) || 0;
                const newTotal = parseFloat(value) || 0;
                const newTax = newTotal - currentSubtotal;
                setTaxAmount(newTax.toFixed(2));
                // Update tax percentage if subtotal exists
                if (currentSubtotal > 0) {
                  const newTaxPercentage = (newTax / currentSubtotal) * 100;
                  setTaxValue(newTaxPercentage);
                }
              }} />)} />
            </View>
          </ExpandableSection>
        </ScrollView>
        <Separator vertical />
        {
          Platform.OS === 'web' &&
          <View style={{ left: -50, alignSelf: "center", flex: 2, margin: 2, minHeight: "120%", transform: [{ scale: 0.8 }] }}>
              <InvoicePreview ref={invoiceRef} htmlContent={htmlContent} onFieldClick={handleFieldClick} />
            </View>
        }
      </XStack>

      {/* Invoice Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            animation="quick"
            opacity={0.6}
            style={{
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(8px)',
              })
            }}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            style={[
              {
                padding: 0,
                minWidth: 500,
                maxWidth: 600,
                maxHeight: "85%",
                borderRadius: 24,
                overflow: 'hidden',
                elevation: 12,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.15,
                shadowRadius: 32,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
                borderWidth: 1,
                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
              },
              Platform.OS === 'web' && {
                backdropFilter: 'blur(24px)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
              }
            ]}
            opacity={1}
            y={0}
          >
            {/* Header */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 20,
              borderBottomWidth: 1,
              borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 10,
                }}>
                  <Feather
                    name="settings"
                    size={20}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <YStack flex={1}>
                  <Dialog.Title style={{
                    fontSize: 20,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 4,
                    margin: 0,
                  }}>
                    Invoice Settings
                  </Dialog.Title>
                  <Dialog.Description style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    fontWeight: '500',
                    margin: 0,
                  }}>
                    Customize invoice display options
                  </Dialog.Description>
                </YStack>
              </XStack>
            </View>

            {/* Content */}
            <View style={{ padding: 24 }}>
              <YStack gap="$4">
                {/* Company Logo */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <XStack alignItems="center" justifyContent="space-between" style={{ marginBottom: 16 }}>
                    <XStack alignItems="center" gap="$3" flex={1}>
                      <View style={{
                        backgroundColor: theme ? "#fef3c7" : "#451a03",
                        borderRadius: 10,
                        padding: 8,
                      }}>
                        <Feather
                          name="image"
                          size={20}
                          color={theme ? "#f59e0b" : "#fbbf24"}
                        />
                      </View>
                      <YStack flex={1}>
                        <Text style={{
                          fontSize: 16,
                          fontWeight: '600',
                          color: theme ? '#1e293b' : '#f1f5f9',
                          marginBottom: 2,
                        }}>
                          Company Logo
                        </Text>
                        <Text style={{
                          fontSize: 12,
                          color: theme ? '#64748b' : '#94a3b8',
                        }}>
                          {invoiceSettings.showLogo ? "Visible on invoice" : "Hidden from invoice"}
                        </Text>
                      </YStack>
                    </XStack>
                    <SwitchWithIcons
                      value={invoiceSettings.showLogo}
                      trackColor={{ false: '#cbd5e1', true: '#3b82f6' }}
                      thumbColor={{ false: '#94a3b8', true: '#ffffff' }}
                      animationDuration={200}
                      style={{
                        transform: [{ scale: 0.9 }],
                        elevation: 2,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.1,
                        shadowRadius: 4,
                      }}
                      icon={{ false: 'eye-off', true: 'eye' }}
                      onValueChange={(value) => {
                        setInvoiceSettings(prev => ({ ...prev, showLogo: value }));
                      }}
                    />
                  </XStack>

                  {/* Logo URL Input and Media Picker */}
                  {invoiceSettings.showLogo && (
                    <YStack gap="$3" style={{ marginTop: 16 }}>
                      <YStack gap="$2">
                        <Text style={{
                          fontSize: 14,
                          fontWeight: '600',
                          color: theme ? '#1e293b' : '#f1f5f9',
                        }}>
                          Logo URL
                        </Text>
                        <TextArea
                          placeholder="Enter logo URL or use media picker"
                          value={customLogoUrl}
                          onChangeText={setCustomLogoUrl}
                          style={{
                            backgroundColor: theme ? '#ffffff' : '#1f2937',
                            borderColor: theme ? '#e5e7eb' : '#4b5563',
                            borderWidth: 1,
                            borderRadius: 8,
                            padding: 12,
                            color: theme ? '#1f2937' : '#f9fafb',
                            fontSize: 14,
                            minHeight: 44,
                          }}
                          placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                        />
                      </YStack>

                      <XStack gap="$3">
                        <Pressable
                          onPress={() => setShowMediaSelector(true)}
                          style={{
                            flex: 1,
                            backgroundColor: theme ? '#3b82f6' : '#2563eb',
                            paddingVertical: 12,
                            paddingHorizontal: 16,
                            borderRadius: 8,
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'row',
                            gap: 8,
                          }}
                        >
                          <Feather name="image" size={18} color="white" />
                          <Text style={{
                            color: 'white',
                            fontWeight: '600',
                            fontSize: 14,
                          }}>
                            Choose from Media
                          </Text>
                        </Pressable>

                        {customLogoUrl ? (
                          <Pressable
                            onPress={() => setCustomLogoUrl('')}
                            style={{
                              backgroundColor: theme ? '#ef4444' : '#dc2626',
                              paddingVertical: 12,
                              paddingHorizontal: 16,
                              borderRadius: 8,
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Feather name="x" size={18} color="white" />
                          </Pressable>
                        ) : null}
                      </XStack>

                      {customLogoUrl ? (
                        <View style={{
                          marginTop: 8,
                          alignItems: 'center',
                        }}>
                          <View style={{
                            borderWidth: 1,
                            borderColor: theme ? '#e5e7eb' : '#4b5563',
                            borderRadius: 8,
                            padding: 8,
                            backgroundColor: theme ? '#ffffff' : '#1f2937',
                          }}>
                            <Image
                              source={{ uri: customLogoUrl }}
                              style={{
                                width: 150,
                                height: 150,
                                resizeMode: 'contain',
                              }}
                              onError={() => {
                                console.error('Failed to load logo image');
                              }}
                            />
                          </View>
                        </View>
                      ) : null}
                    </YStack>
                  )}
                </View>

                {/* Signature Section */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <XStack alignItems="center" justifyContent="space-between">
                    <XStack alignItems="center" gap="$3" flex={1}>
                      <View style={{
                        backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
                        borderRadius: 10,
                        padding: 8,
                      }}>
                        <Feather
                          name="edit-3"
                          size={20}
                          color={theme ? "#3b82f6" : "#60a5fa"}
                        />
                      </View>
                      <YStack flex={1}>
                        <Text style={{
                          fontSize: 16,
                          fontWeight: '600',
                          color: theme ? '#1e293b' : '#f1f5f9',
                          marginBottom: 2,
                        }}>
                          Signature Section
                        </Text>
                        <Text style={{
                          fontSize: 12,
                          color: theme ? '#64748b' : '#94a3b8',
                        }}>
                          {invoiceSettings.showSignature ? "Show signature fields" : "Hide signature fields"}
                        </Text>
                      </YStack>
                    </XStack>
                    <SwitchWithIcons
                      value={invoiceSettings.showSignature}
                      trackColor={{ false: '#cbd5e1', true: '#3b82f6' }}
                      thumbColor={{ false: '#94a3b8', true: '#ffffff' }}
                      animationDuration={200}
                      style={{
                        transform: [{ scale: 0.9 }],
                        elevation: 2,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.1,
                        shadowRadius: 4,
                      }}
                      icon={{ false: 'eye-off', true: 'eye' }}
                      onValueChange={(value) => {
                        setInvoiceSettings(prev => ({ ...prev, showSignature: value }));
                      }}
                    />
                  </XStack>
                </View>

                {/* Bank Details */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <XStack alignItems="center" justifyContent="space-between">
                    <XStack alignItems="center" gap="$3" flex={1}>
                      <View style={{
                        backgroundColor: theme ? "#d1fae5" : "#064e3b",
                        borderRadius: 10,
                        padding: 8,
                      }}>
                        <Feather
                          name="credit-card"
                          size={20}
                          color={theme ? "#10b981" : "#34d399"}
                        />
                      </View>
                      <YStack flex={1}>
                        <Text style={{
                          fontSize: 16,
                          fontWeight: '600',
                          color: theme ? '#1e293b' : '#f1f5f9',
                          marginBottom: 2,
                        }}>
                          Bank Details
                        </Text>
                        <Text style={{
                          fontSize: 12,
                          color: theme ? '#64748b' : '#94a3b8',
                        }}>
                          {invoiceSettings.showBankDetails ? "Show banking information" : "Hide banking information"}
                        </Text>
                      </YStack>
                    </XStack>
                    <SwitchWithIcons
                      value={invoiceSettings.showBankDetails}
                      trackColor={{ false: '#cbd5e1', true: '#3b82f6' }}
                      thumbColor={{ false: '#94a3b8', true: '#ffffff' }}
                      animationDuration={200}
                      style={{
                        transform: [{ scale: 0.9 }],
                        elevation: 2,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.1,
                        shadowRadius: 4,
                      }}
                      icon={{ false: 'eye-off', true: 'eye' }}
                      onValueChange={(value) => {
                        setInvoiceSettings(prev => ({ ...prev, showBankDetails: value }));
                      }}
                    />
                  </XStack>
                </View>

                {/* Notes Section */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <XStack alignItems="center" justifyContent="space-between">
                    <XStack alignItems="center" gap="$3" flex={1}>
                      <View style={{
                        backgroundColor: theme ? "#ede9fe" : "#312e81",
                        borderRadius: 10,
                        padding: 8,
                      }}>
                        <Feather
                          name="file-text"
                          size={20}
                          color={theme ? "#8b5cf6" : "#a78bfa"}
                        />
                      </View>
                      <YStack flex={1}>
                        <Text style={{
                          fontSize: 16,
                          fontWeight: '600',
                          color: theme ? '#1e293b' : '#f1f5f9',
                          marginBottom: 2,
                        }}>
                          Notes Section
                        </Text>
                        <Text style={{
                          fontSize: 12,
                          color: theme ? '#64748b' : '#94a3b8',
                        }}>
                          {invoiceSettings.showNotes ? "Show notes on invoice" : "Hide notes from invoice"}
                        </Text>
                      </YStack>
                    </XStack>
                    <SwitchWithIcons
                      value={invoiceSettings.showNotes}
                      trackColor={{ false: '#cbd5e1', true: '#3b82f6' }}
                      thumbColor={{ false: '#94a3b8', true: '#ffffff' }}
                      animationDuration={200}
                      style={{
                        transform: [{ scale: 0.9 }],
                        elevation: 2,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.1,
                        shadowRadius: 4,
                      }}
                      icon={{ false: 'eye-off', true: 'eye' }}
                      onValueChange={(value) => {
                        setInvoiceSettings(prev => ({ ...prev, showNotes: value }));
                      }}
                    />
                  </XStack>
                </View>
              </YStack>
            </View>

            {/* Footer Actions */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 16,
              borderTopWidth: 1,
              borderTopColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack gap="$3" justifyContent="flex-end">
                <Dialog.Close asChild>
                  <Pressable
                    onPress={() => setSettingsOpen(false)}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#6b7280',
                      minWidth: 100,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{
                      color: theme ? '#64748b' : '#d1d5db',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Close
                    </Text>
                  </Pressable>
                </Dialog.Close>
              </XStack>
            </View>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>

      {/* Media Selector Modal */}
      <MediaSelector
        visible={showMediaSelector}
        onClose={() => setShowMediaSelector(false)}
        onSelect={(imageUrl) => {
          setCustomLogoUrl(imageUrl);
          setShowMediaSelector(false);
        }}
      />
    </DefaultScreen>
  )
}
export default InvoiceScreen;